'use client';

import React, { useEffect, useState, useMemo } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  ShieldCheck,
  Clock,
  Printer,
  Copy,
  Check,
  ChevronRight,
  ChevronDown,
  FileText,
  Lock,
  RotateCcw,
  Truck,
  CreditCard,
  ShieldAlert,
  Link2,
  Mail,
  Phone,
  MapPin,
  Building,
  Layers,
  ArrowUpRight,
} from 'lucide-react';
import { businessInfo } from '@/lib/business-info';
import { SectionHeader, GradientText } from '@/features/marketing/components/SectionHeader';
import { BreadcrumbSchema, WebPageSchema } from '@/components/StructuredData';
import { siteUrl } from '@/lib/config';
import { isPathActive } from '@/lib/utils';
import type { Policy } from './legalPolicies';

type PolicyPageProps = {
  policy: Policy;
  children?: React.ReactNode;
};

const LEGAL_NAV_ITEMS = [
  { href: '/privacy', label: 'Privacy Policy', icon: Lock },
  { href: '/terms', label: 'Terms & Conditions', icon: FileText },
  { href: '/pricing-policy', label: 'Pricing & Billing Policy', icon: CreditCard },
  { href: '/refund-policy', label: 'Refund & Cancellation', icon: RotateCcw },
  { href: '/subscription-policy', label: 'Subscription Policy', icon: FileText },
  { href: '/shipping-policy', label: 'Shipping & Delivery', icon: Truck },
  { href: '/acceptable-use', label: 'Acceptable Use Policy', icon: ShieldAlert },
  { href: '/cookie-policy', label: 'Cookie Policy', icon: FileText },
  { href: '/data-deletion', label: 'Data Deletion Policy', icon: FileText },
  { href: '/legal/subprocessors', label: 'Subprocessors List', icon: Layers },
  { href: '/legal', label: 'Legal Center Hub', icon: Building },
];

function getSectionId(title: string, index: number): string {
  const cleanTitle = title
    .toLowerCase()
    .replace(/^\d+\.\s*/, '')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
  return cleanTitle ? `section-${cleanTitle}` : `section-${index + 1}`;
}

function calculateReadingTime(policy: Policy): number {
  let text = '';
  if (typeof policy.intro === 'string') text += policy.intro + ' ';
  for (const s of policy.sections) {
    text += s.title + ' ';
    if (typeof s.content === 'string') text += s.content + ' ';
    if (s.items) {
      for (const item of s.items) {
        if (typeof item === 'string') text += item + ' ';
      }
    }
  }
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 220));
}

export function PolicyPage({ policy, children }: PolicyPageProps) {
  const pathname = usePathname();
  const [copied, setCopied] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState<string>('');
  const [mobileTocOpen, setMobileTocOpen] = useState(false);

  const readingTimeMinutes = useMemo(() => calculateReadingTime(policy), [policy]);

  const sectionsWithIds = useMemo(() => {
    return policy.sections.map((section, idx) => ({
      ...section,
      id: getSectionId(section.title, idx),
    }));
  }, [policy.sections]);

  // IntersectionObserver for table of contents active highlighting
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSectionId(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px' }
    );

    sectionsWithIds.forEach((sec) => {
      const el = document.getElementById(sec.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sectionsWithIds]);

  const handleCopyLink = () => {
    if (typeof window !== 'undefined') {
      void navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePrint = () => {
    if (typeof window !== 'undefined') {
      window.print();
    }
  };

  const currentUrl = siteUrl(pathname);

  return (
    <div className="bg-background text-on-surface min-h-screen selection:bg-primary/20 selection:text-primary">
      {/* Structured Data for SEO */}
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Legal Center', url: '/legal' },
          { name: policy.title, url: pathname },
        ]}
      />
      <WebPageSchema title={`${policy.title} — PostBrix`} description={policy.description} url={currentUrl} />

      {/* --- HERO HEADER --- */}
      <section className="border-outline-variant/60 bg-surface-container/60 relative overflow-hidden border-b pt-24 sm:pt-28 pb-14 backdrop-blur-md print:hidden">
        {/* Ambient Gradients */}
        <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          <div className="bg-primary/8 absolute -top-32 left-1/2 h-[500px] w-[900px] -translate-x-1/2 rounded-full blur-[140px]" />
          <div className="bg-secondary/5 absolute top-10 right-0 h-[350px] w-[500px] rounded-full blur-[100px]" />
        </div>

        <div className="relative z-10 mx-auto max-w-6xl px-6 text-center">
          {/* Breadcrumb Navigation */}
          <nav aria-label="Breadcrumb" className="mb-6 flex justify-center">
            <ol className="bg-surface-container-high/60 border-outline-variant/50 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-medium backdrop-blur-sm">
              <li>
                <Link href="/" className="text-on-surface-variant/70 hover:text-primary transition-colors">
                  Home
                </Link>
              </li>
              <li className="text-on-surface-variant/40">/</li>
              <li>
                <Link href="/legal" className="text-on-surface-variant/70 hover:text-primary transition-colors">
                  Legal Center
                </Link>
              </li>
              <li className="text-on-surface-variant/40">/</li>
              <li className="text-primary font-semibold truncate max-w-[200px] sm:max-w-none">
                {policy.title}
              </li>
            </ol>
          </nav>

          <SectionHeader
            as="h1"
            eyebrow="PostBrix Official Policy"
            eyebrowIcon={<ShieldCheck size={13} className="text-primary" />}
            heading={<GradientText>{policy.title}</GradientText>}
            description={policy.description}
            size="display"
            maxWidth="max-w-3xl"
          />

          {/* Meta Info Bar */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-xs font-semibold">
            <div className="bg-surface-container-high border-outline-variant/60 text-on-surface-variant flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 shadow-sm">
              <span className="bg-emerald-500 inline-block h-2 w-2 rounded-full animate-pulse" />
              <span>{policy.updatedLabel}</span>
            </div>

            <div className="bg-surface-container-high border-outline-variant/60 text-on-surface-variant flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 shadow-sm">
              <Clock size={13} className="text-primary shrink-0" />
              <span>{readingTimeMinutes} min read</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleCopyLink}
                className="bg-surface-container-high hover:bg-surface-container-highest border-outline-variant/60 text-on-surface hover:text-primary flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 transition-all duration-200 shadow-sm active:scale-95"
                title="Copy link to clipboard"
              >
                {copied ? (
                  <>
                    <Check size={13} className="text-emerald-500" />
                    <span className="text-emerald-500 font-bold">Copied</span>
                  </>
                ) : (
                  <>
                    <Copy size={13} />
                    <span>Share Link</span>
                  </>
                )}
              </button>

              <button
                onClick={handlePrint}
                className="bg-surface-container-high hover:bg-surface-container-highest border-outline-variant/60 text-on-surface hover:text-primary flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 transition-all duration-200 shadow-sm active:scale-95"
                title="Print this document"
              >
                <Printer size={13} />
                <span className="hidden sm:inline">Print Document</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* --- MOBILE ACCORDION TABLE OF CONTENTS --- */}
      <div className="border-outline-variant/60 bg-surface-container/90 sticky top-16 z-20 border-b backdrop-blur-md lg:hidden print:hidden">
        <div className="mx-auto max-w-5xl px-6">
          <button
            onClick={() => setMobileTocOpen(!mobileTocOpen)}
            aria-expanded={mobileTocOpen}
            aria-label="Table of contents for this policy"
            className="flex w-full items-center justify-between py-3 text-xs font-bold text-on-surface cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <Layers size={14} className="text-primary" />
              Jump to Section ({sectionsWithIds.length} topics)
            </span>
            <ChevronDown
              size={16}
              className={`transition-transform duration-200 ${mobileTocOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {mobileTocOpen && (
            <nav className="border-outline-variant/40 space-y-1 border-t py-3 max-h-60 overflow-y-auto">
              {sectionsWithIds.map((sec) => (
                <a
                  key={sec.id}
                  href={`#${sec.id}`}
                  onClick={() => setMobileTocOpen(false)}
                  className={`block rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                    activeSectionId === sec.id
                      ? 'bg-primary/10 text-primary font-bold'
                      : 'text-on-surface-variant/80 hover:bg-surface-container-high hover:text-on-surface'
                  }`}
                >
                  {sec.title}
                </a>
              ))}
            </nav>
          )}
        </div>
      </div>

      {/* --- MAIN LAYOUT (ARTICLE + SIDEBARS) --- */}
      <div className="mx-auto grid max-w-[1440px] gap-8 xl:gap-12 px-4 sm:px-8 py-12 lg:grid-cols-[250px_1fr_280px] lg:py-16">
        {/* --- LEFT SIDEBAR: IN-PAGE TABLE OF CONTENTS --- */}
        <aside className="hidden lg:block">
          <div className="sticky top-28 space-y-4">
            <div className="border-outline-variant/60 bg-surface-container-lowest/80 rounded-2xl border p-4 shadow-sm backdrop-blur-sm">
              <h3 className="text-on-surface-variant/60 mb-3 flex items-center gap-1.5 px-2 text-[11px] font-black tracking-widest uppercase">
                <Layers size={12} className="text-primary" />
                On This Page
              </h3>
              <nav className="space-y-1">
                {sectionsWithIds.map((sec) => {
                  const isActive = activeSectionId === sec.id;
                  return (
                    <a
                      key={sec.id}
                      href={`#${sec.id}`}
                      className={`group flex items-start gap-2 rounded-lg px-2.5 py-1.5 text-xs transition-all duration-200 ${
                        isActive
                          ? 'bg-primary/10 text-primary font-bold shadow-xs'
                          : 'text-on-surface-variant/75 hover:bg-surface-container-high hover:text-on-surface font-medium'
                      }`}
                    >
                      <span
                        className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full transition-transform ${
                          isActive ? 'bg-primary scale-125' : 'bg-outline-variant group-hover:bg-on-surface-variant'
                        }`}
                      />
                      <span className="line-clamp-2 leading-tight">{sec.title}</span>
                    </a>
                  );
                })}
              </nav>
            </div>
          </div>
        </aside>

        {/* --- MAIN ARTICLE CONTENT --- */}
        <article className="min-w-0">
          {/* Intro Box */}
          <div className="border-outline-variant/60 bg-surface-container-low/70 mb-12 rounded-3xl border p-6 sm:p-8 shadow-sm backdrop-blur-xs">
            <div className="text-on-surface text-base sm:text-lg leading-relaxed font-medium">
              {policy.intro}
            </div>
          </div>

          {/* Sections List */}
          <div className="space-y-12">
            {sectionsWithIds.map((section, idx) => (
              <section
                key={section.id}
                id={section.id}
                className="group scroll-mt-28 border-outline-variant/40 border-b pb-10 last:border-b-0"
              >
                <div className="mb-4 flex items-center gap-3">
                  <span className="bg-primary/10 text-primary font-mono text-xs font-bold rounded-lg px-2.5 py-1 shrink-0">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <h2 className="text-on-surface text-xl sm:text-2xl font-extrabold tracking-tight flex items-center gap-2">
                    <span>{section.title}</span>
                    <a
                      href={`#${section.id}`}
                      className="text-on-surface-variant/30 hover:text-primary transition-opacity opacity-0 group-hover:opacity-100"
                      title="Direct link to section"
                    >
                      <Link2 size={18} />
                    </a>
                  </h2>
                </div>

                {section.content && (
                  <div className="text-on-surface-variant/90 text-base leading-relaxed font-medium prose prose-slate dark:prose-invert max-w-none">
                    {section.content}
                  </div>
                )}

                {section.items && section.items.length > 0 && (
                  <ul className="mt-5 space-y-3">
                    {section.items.map((item, itemIdx) => (
                      <li
                        key={`${section.id}-item-${itemIdx}`}
                        className="bg-surface-container-lowest/60 border-outline-variant/40 flex items-start gap-3.5 rounded-2xl border p-4 transition-colors hover:border-outline-variant"
                      >
                        <span className="bg-primary/10 text-primary mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold">
                          ✓
                        </span>
                        <div className="text-on-surface-variant text-sm leading-relaxed font-medium min-w-0">
                          {item}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          {/* Children Slot (e.g. Cookie Consent Button) */}
          {children && (
            <div className="mt-12 border-outline-variant/60 border-t pt-12">
              {children}
            </div>
          )}

          {/* Footer Callout Card */}
          <div className="border-outline-variant/60 bg-gradient-to-br from-surface-container-low to-surface-container-lowest mt-16 rounded-3xl border p-8 shadow-md relative overflow-hidden">
            <div className="bg-primary/5 absolute -right-10 -bottom-10 h-40 w-40 rounded-full blur-2xl pointer-events-none" />
            <h3 className="text-on-surface text-lg font-extrabold tracking-tight mb-2 flex items-center gap-2">
              <ShieldCheck size={20} className="text-primary" />
              Need legal clarification or have questions?
            </h3>
            <p className="text-on-surface-variant/80 text-sm font-medium leading-relaxed mb-6">
              Our legal and privacy team is available to assist you with compliance inquiries, Data Processing Addendums (DPA), or billing details.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <a
                href={`mailto:${businessInfo.privacyEmail}`}
                className="bg-primary text-on-primary hover:opacity-90 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold transition-all shadow-sm"
              >
                <Mail size={14} />
                Contact Privacy Team
              </a>
              <Link
                href="/legal"
                className="border-outline-variant bg-surface-container-high hover:bg-surface-container-highest text-on-surface inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-xs font-bold transition-all"
              >
                View All Legal Docs
                <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </article>

        {/* --- RIGHT SIDEBAR: POLICY DIRECTORY & BUSINESS CONTACT --- */}
        <aside className="space-y-6">
          {/* Policy Directory Navigation */}
          <div className="border-outline-variant/60 bg-surface-container-lowest/80 rounded-2xl border p-5 shadow-sm backdrop-blur-sm sticky top-28">
            <h3 className="text-on-surface-variant/60 mb-3.5 px-2 text-[11px] font-black tracking-widest uppercase">
              Legal Documents
            </h3>
            <nav className="space-y-1">
              {LEGAL_NAV_ITEMS.map((item) => {
                const isActive = isPathActive(pathname, item.href);
                const IconComp = item.icon;
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={`flex items-center justify-between rounded-xl px-3 py-2 text-xs font-semibold transition-all duration-200 ${
                      isActive
                        ? 'bg-primary text-on-primary shadow-sm'
                        : 'text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface'
                    }`}
                  >
                    <span className="flex items-center gap-2.5 truncate">
                      <IconComp size={14} className={isActive ? 'text-on-primary' : 'text-primary shrink-0'} />
                      <span className="truncate">{item.label}</span>
                    </span>
                    {isActive ? (
                      <ChevronRight size={14} className="shrink-0" />
                    ) : null}
                  </Link>
                );
              })}
            </nav>

            <div className="border-outline-variant/40 mt-6 border-t pt-5 space-y-4">
              <h4 className="text-on-surface-variant/60 px-2 text-[10px] font-black tracking-widest uppercase">
                Verified Business Entity
              </h4>

              <div className="space-y-3 text-xs">
                <div className="flex items-start gap-2.5 px-2">
                  <Building size={14} className="text-primary shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-on-surface font-bold leading-tight">{businessInfo.legalName}</p>
                    <p className="text-on-surface-variant/60 text-[11px] font-medium">{businessInfo.productName}</p>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 px-2">
                  <Mail size={14} className="text-primary shrink-0 mt-0.5" />
                  <a
                    href={`mailto:${businessInfo.privacyEmail}`}
                    className="text-primary hover:underline font-bold truncate"
                  >
                    {businessInfo.privacyEmail}
                  </a>
                </div>

                {businessInfo.phone && (
                  <div className="flex items-start gap-2.5 px-2">
                    <Phone size={14} className="text-primary shrink-0 mt-0.5" />
                    <a
                      href={`tel:${businessInfo.phone.replace(/\s/g, '')}`}
                      className="text-on-surface hover:text-primary font-medium"
                    >
                      {businessInfo.phone}
                    </a>
                  </div>
                )}

                {businessInfo.address && businessInfo.address.length > 0 && (
                  <div className="flex items-start gap-2.5 px-2">
                    <MapPin size={14} className="text-primary shrink-0 mt-0.5" />
                    <address className="text-on-surface-variant/80 text-[11px] font-medium not-italic leading-relaxed">
                      {businessInfo.address.join(', ')}
                    </address>
                  </div>
                )}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
